import { ChangeDetectionStrategy, Component, Input, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { debounceTime, tap } from 'rxjs/operators';
import { UntilDestroy } from '@ngneat/until-destroy';
import { ContactsData } from '@bazis/form/models/form-element.types';
import { TemplateObservable } from '@bazis/shared/classes/template-observable';
import { BazisInputContactsComponent } from '@bazis/form/components/complex/input-contacts.component';
import { ALPHABET_DASH_SPACE_PATTERN } from '@app/configuration.service';

@UntilDestroy()
@Component({
    selector: 'app-input-contacts',
    templateUrl: './input-contacts.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class AppInputContactsComponent extends BazisInputContactsComponent {
    valueChanges$ = this.contactsForm.valueChanges.pipe(
        debounceTime(500),
        tap((value) => {
            this.onChange(value.contacts?.length ? value.contacts[0] : null);
        }),
    );

    required = new TemplateObservable(true);

    public writeValue(value): void {
        if (!this.ngControl.control) return;
        this.contacts.clear({ emitEvent: false });

        this.setValidators();

        if (!value) {
            this.init();
            return;
        } else if (!Array.isArray(value)) {
            value = [value];
        }
        value.forEach((contact) => {
            this.contacts.push(this.generateContact({ ...contact }));
        });
    }

    generateContact(data: ContactsData = null) {
        return new FormGroup(
            {
                first_name: new FormControl(data?.first_name, [
                    Validators.required,
                    Validators.pattern(ALPHABET_DASH_SPACE_PATTERN),
                ]),
                last_name: new FormControl(data?.last_name, [
                    Validators.required,
                    Validators.pattern(ALPHABET_DASH_SPACE_PATTERN),
                ]),
                middle_name: new FormControl(data?.middle_name, [
                    Validators.pattern(ALPHABET_DASH_SPACE_PATTERN),
                ]),
                phone: new FormControl(data?.phone, [
                    Validators.required,
                    Validators.pattern(this.phonePattern),
                ]),
                phone_additional: new FormControl(data?.phone_additional, [
                    Validators.max(999999999),
                ]),
                position: new FormControl(data?.position),
                email: new FormControl(data?.email),
            },
            [this.validateContactItem],
        );
    }

    validate({ value }: FormControl) {
        if (this.contactsForm.status === 'VALID') {
            return null;
        }

        let componentError;
        this.contacts.controls.forEach((contact, index) => {
            if (contact.errors?.contactLastNameEmpty) {
                componentError = {
                    requiredFieldId: this.anchorRoot + '-last_name-' + index,
                    titleKey: 'form.title.contact.last_name',
                };
            }
            if (contact.errors?.contactFirstNameEmpty) {
                componentError = {
                    requiredFieldId: this.anchorRoot + '-first_name-' + index,
                    titleKey: 'form.title.contact.first_name',
                };
            }
            if (contact.errors?.contactPhoneEmpty) {
                componentError = {
                    requiredFieldId: this.anchorRoot + '-phone-' + index,
                    titleKey: 'form.title.contact.phone',
                };
            }
        });

        return {
            componentError: componentError
                ? componentError
                : this.contacts.controls.map((contact) => contact.status === 'VALID'),
        };
    }

    validateContactItem(item) {
        if (!item.controls.last_name.value) return { contactLastNameEmpty: true };
        if (!item.controls.first_name.value) return { contactFirstNameEmpty: true };
        if (!item.controls.phone.value) return { contactPhoneEmpty: true };

        return null;
    }
}
